import "./LauncherPage.scss";
import logoNew from "../../../assets/icons/logo-name.svg";
import { useEffect, useRef, useState } from "react";
import { launcherApiService, promoApiService, } from "../../../platform/api";
import { LauncherApiError } from "../../../platform/api/launcherApiService";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ILauncherGame, ILauncherUrlCheckResult } from "../../../platform/interfaces/launcher";
import { authStore, fortuneWheelStore, promoStore, translationStore, utilsStore, } from "../../../platform/stores";
import { IConnectionLostOptions } from "../../../platform/stores/utilsStore";
import { parseJwt } from "../../../platform/utils";
import posthog from "posthog-js";
import { translationApiService } from "../../../platform/api/translationApiService";
import gsap from "gsap";
import {
  fortuneWheelService,
  launchSessionService,
  tournamentService,
  userSessionService
} from "../../../platform/services";

const SOMETHING_WENT_WRONG = 'SOMETHING_WENT_WRONG';
const SESSION_REPLACED = 'SESSION_REPLACED';

export const LauncherPage = () => {
  const navigate = useNavigate();
  const gameFrameRef = useRef(null);
  const [searchParams] = useSearchParams();
  const [loadingHidden, setLoadingHidden] = useState(false);
  const { setFreeBetEndData, setFreeBetAwardData } = utilsStore;

  useEffect(() => {
    const url = new URL(window.location.href);
    window.addEventListener("message", onMessage);
    launcherApiService
      .checkUrl(url.href)
      .then((response) => {
        if (response.errorMessage) {
          handleCheckError(response.errorMessage);
        } else if (response.maintenance) {
          window.location.href = `/launcher/maintenance?end=${response.maintenance.end}`;
        } else {
          handleUrl(response.url);
          handleAccessToken(response.accessToken);
          startSession(response);
          initPostHog(response.accessToken);

          userSessionService.connect(response.game?.id, response.partner?.id, onSessionReplaced);

          if (response.game) {
            handleGame(response.game);
            checkPromo(response.game!.id, response.partner!.id);
            fortuneWheelService.setData(response.game!.id, response.partner!.id);
            tournamentService.checkAndSetActiveTournament(response.game!.id, response.partner!.id).then();
          }

          if (response.language) {
            getTranslations(response.language.code);
          }
        }
      })
      .catch((error) => {
        console.log(error, JSON.stringify(error))
        if (!(error instanceof LauncherApiError)) {
          showConnectionLost(SOMETHING_WENT_WRONG, 'LAUNCH_ERROR');
        } else if (error.status === 0) {
          showConnectionLost('NETWORK_ERROR', 'LAUNCH_UNREACHABLE');
        } else {
          showConnectionLost(SOMETHING_WENT_WRONG, `LAUNCH_HTTP_${error.status}`);
        }
      })
  }, []);

  // Also recorded in PostHog, so failures can be counted without waiting for someone to send a screenshot.
  const showConnectionLost = (message: string, code: string, options?: IConnectionLostOptions) => {
    posthog.capture('connection_lost', { code, message, gameId: utilsStore.gameId });
    utilsStore.setConnectionLost(message, code, options);
  };

  const onSessionReplaced = (internalSessionId: string) => {
    const ownSessionId = authStore.accessToken ? parseJwt(authStore.accessToken).internalSessionId : null;
    if (internalSessionId !== ownSessionId) {
      endSessionElsewhere();
    }
  };

  // The player opened a game on another device or tab, which ended this session in Identity. Close every
  // connection and leave no way back from here: reconnecting would just take the session from the other device.
  const endSessionElsewhere = () => {
    // Both the tracker's push and a refresh answered with 409 can report it.
    if (utilsStore.connectionLost?.code === SESSION_REPLACED) return;

    launchSessionService.stop();
    userSessionService.disconnect();
    unloadGame();
    showConnectionLost('LOGGED_IN_ELSEWHERE', SESSION_REPLACED, { title: 'SESSION_ENDED', canReconnect: false });
  };

  // A failed launch can be retried, but an unknown game or partner will not fix itself on reconnect.
  const handleCheckError = (errorMessage: string) => {
    if (errorMessage === SOMETHING_WENT_WRONG) {
      showConnectionLost(errorMessage, 'LAUNCH_FAILED');
    } else {
      navigate(`/error?message=${errorMessage}`, {
        replace: true,
      });
    }
  };

  const initPostHog = (accessToken: string) => {
    if (!accessToken) return;

    const payload = parseJwt(accessToken);
    posthog.identify(payload.sub, {
      partnerId: payload.partnerId,
      sessionId: payload.sessionId,
      playerExternalId: payload.playerExternalId,
    });
  };

  const onMessage = (e: any) => {
    const { data } = e.data;
    switch (e.data.event) {
      case 'game-preloaded':
        postAccessToken();
        window.parent?.postMessage({ event: "ast-game-preloaded" }, "*");
        break;
      case 'game-loaded':
        setLoadingHidden(true);
        fortuneWheelStore.setGameLoadingComplete(true);
        break;
      case 'open-game':
        openGame(data.id);
        break;
      case 'free-bet-award':
        if (data.source === 3) {
          gsap.delayedCall(3, () => {
            setFreeBetAwardData(data);
            fortuneWheelStore.setDailyWheelModalIsOpen(false)
          });
        } else {
          setFreeBetAwardData(data);
        }
        promoStore.setPromoModalIsOpen(false);
        break;
      case 'free-bet-end':
        setFreeBetEndData(data);
        break;
      case 'exit':
        exit();
        break;
      case 'capture-event':
        posthog.capture('game_behavior', data);
        break;
      case 'apply-skin':
        postMessageToFrame({ event: 'apply-skin', skin: data });
        break;
    }
  };

  const openGame = (id: number | string) => {
    const urlObject = new URL(window.location.href);
    urlObject.searchParams.set("gameId", String(id));
    window.location.href = urlObject.toString();
  };

  const exit = () => {
    const exitUrl = searchParams.get("exitUrl");
    if (exitUrl) {
      window.location.href = exitUrl;
    }
  }

  const postAccessToken = () => {
    postMessageToFrame({ event: 'access-token-received', accessToken: authStore.accessToken ?? "" });
  }

  const postMessageToFrame = (data: any) => {
    const gameFrame = gameFrameRef.current as unknown as HTMLIFrameElement;
    if (gameFrame) {
      gameFrame.contentWindow!.postMessage(data, "*");
    }
  };

  const handleUrl = (url: string) => {
    const gameFrame = gameFrameRef.current as unknown as HTMLIFrameElement;
    if (gameFrame) {
      gameFrame.src = url;
    }
  };

  // Navigating the frame away closes the game's own connections (its game socket).
  const unloadGame = () => {
    const gameFrame = gameFrameRef.current as unknown as HTMLIFrameElement;
    if (gameFrame) {
      gameFrame.src = 'about:blank';
      gameFrame.style.visibility = 'hidden';
    }
  };

  const handleAccessToken = (token: string) => {
    authStore.setAccessToken(token);
  };

  const startSession = (response: ILauncherUrlCheckResult) => {
    // Demo launches have no session to keep alive.
    if (!response.refreshToken) return;

    authStore.setRefreshToken(response.refreshToken);
    launchSessionService.start(response.refreshToken, {
      onToken: (token) => {
        handleAccessToken(token.accessToken);
        authStore.setRefreshToken(token.refreshToken);
        postAccessToken();
      },
      onFailure: (message, code) => code === SESSION_REPLACED ? endSessionElsewhere() : showConnectionLost(message, code),
    });
  };

  const handleGame = (game: ILauncherGame | null) => {
    if (!game) return;
    document.title = game.name;
    utilsStore.setGameId(game.id);
  };

  const checkPromo = (gameId: number, partnerId: number) => {
    promoApiService.check(gameId, partnerId).then((data) => {
      promoStore.setPromoButtonIsVisible(data);
    });
  };

  const getTranslations = (languageCode: string) => {
    translationApiService.getTranslations(languageCode)
      .then((data) => {
        translationStore.setTranslations(data);
      });
  };

  return (
    <div className="launcher-page">
      {!loadingHidden && (
        <div className="loading">
          <div className="loading-center">
            <div className="logo">
              <img src={logoNew} alt=""/>
            </div>
            <div className="loader-line"></div>
          </div>
        </div>
      )}

      <iframe
        className="game-frame"
        ref={gameFrameRef}
        allow="clipboard-read; clipboard-write"
      ></iframe>
    </div>
  );
};
